<template>
  <div
    class="overall-height max-h-screen pb-4 grid grid-rows-[auto_1fr] bg-pitch-black text-total-white"
  >
    <!-- Header -->
    <div
      class="flex w-full justify-between items-center px-4 p-3 border border-x-0 border-t-0 border-b-1 border-prime-golden bg-pitch-black-900"
    >
      <a href="/" class="text-prime-golden font-avenir-medium mb-[-4px]"
        ><img
          class="w-24 h-24 -my-8 -ml-[10px]"
          src="@/assets/images/mason-logo.svg"
          alt="Mason Logo"
      /></a>
      <!-- <a href="/signup" class="text-prime-golden">Sign Up</a> -->
    </div>

    <!-- Form Section -->
    <div
      class="form-section w-full pt-6 flex flex-col justify-start lg:justify-center items-start mx-auto max-w-md px-4"
    >
      <h1 class="text-4xl font-bold my-6">Log In</h1>
      <form class="w-full" @submit.prevent="onSubmit">
        <!-- Email Field -->
        <div class="mb-3">
          <label class="flex justify-start text-sm font-medium mb-1" for="email">
            Email
          </label>
          <input
            id="email"
            v-model="email"
            @input="errors.email = ''"
            type="email"
            class="w-full p-3 rounded-lg bg-dark-grey text-total-white focus:outline-none focus:border-prime-golden"
            placeholder="Email"
          />
          <!-- Email Error Message -->
          <div v-if="errors.email" class="text-deep-rust px-1 text-left text-sm mt-1">
            {{ errors.email }}
          </div>
        </div>

        <!-- Password Field -->
        <div class="mb-6 relative">
          <label class="flex justify-start text-sm font-medium mb-1" for="password">
            Password
          </label>
          <div class="relative w-full flex items-center">
            <input
              id="password"
              v-model="password"
              @input="errors.password = ''"
              :type="showPassword ? 'text' : 'password'"
              class="w-full p-3 rounded-lg bg-dark-grey text-total-white focus:outline-none focus:border-prime-golden pr-10"
              placeholder="Password"
            />
            <i
              class="fa fa-eye absolute top-1/2 right-3 transform -translate-y-1/2 text-ash-grey"
              v-if="!showPassword"
              @click="showPassword = true"
            ></i>
            <i
              class="fa fa-eye-slash absolute top-1/2 right-3 transform -translate-y-1/2 text-light-grey"
              v-if="showPassword"
              @click="showPassword = false"
            ></i>
          </div>
          <!-- Password Error Message -->
          <div v-if="errors.password" class="text-deep-rust px-1 text-left text-sm mt-1">
            {{ errors.password }}
          </div>
        </div>

        <!-- Submit Button -->
        <button
          type="submit"
          class="w-full py-2 rounded-full bg-prime-golden text-black font-semibold text-lg focus:outline-none hover:bg-prime-golden-500 transition duration-300 ease-in-out"
          :class="{ 'opacity-50': isLoading }"
          :disabled="isLoading"
        >
          Continue
        </button>

        <!-- General Error Message Display -->
        <div v-if="errorMessage" class="text-deep-rust px-1 mt-2 text-center">
          {{ errorMessage }}
        </div>
      </form>

      <!-- Footer -->
      <div class="text-center w-full mt-8 text-total-white-500 text-sm">
        <a :href="termsUrl" target="_blank" class="hover:text-prime-golden"
          >Terms of Service</a
        >
        <span class="mx-2">|</span>
        <a :href="privacyUrl" target="https://www.mason.tech/privacy-policy" class="hover:text-prime-golden"
          >Privacy Policy</a
        >
        <p class="mt-4">&copy; 2024 Mason Intelligence, Inc.</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useAuthStore } from "@/stores/auth";
import { useRouter, useRoute } from "vue-router";

const email = ref("");
const password = ref("");
const errorMessage = ref("");
const errors = ref({}); // Reactive object to store field errors
const showPassword = ref(false);

const termsUrl = process.env.VUE_APP_TERMS_URL;
const privacyUrl = process.env.VUE_APP_PRIVACY_POLICY_URL;

const authStore = useAuthStore();
const router = useRouter();
const route = useRoute();

const isLoading = ref(false);

onMounted(() => {
  if (localStorage.getItem("access_token")) {
    router.push({ name: "ProjectsPage" });
    return;
  }

  if (route.query.email) {
    email.value = route.query.email;
  }
});

// Helper function to validate email format
const isValidEmail = (email) => {
  const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@(([^<>()[\]\\.,;:\s@"]+\.)+[^<>()[\]\\.,;:\s@"]{2,})$/i;
  return re.test(email);
};

const validateForm = () => {
  errors.value = {}; // Clear previous errors

  // Validate email
  if (!email.value) {
    errors.value.email = "Email is required.";
  } else if (!isValidEmail(email.value)) {
    errors.value.email = "Please enter a valid email address.";
  }

  // Validate password
  if (!password.value) {
    errors.value.password = "Password is required.";
  }

  // Return true if there are no errors
  return Object.keys(errors.value).length === 0;
};

const onSubmit = async () => {
  isLoading.value = true;
  // Validate the form before submitting
  if (!validateForm()) {
    return;
  }

  try {
    await authStore.login({
      email: email.value,
      password: password.value,
    });
  } catch (error) {
    // Handle login error
    if (error.response && error.response.data && error.response.data.detail) {
      errorMessage.value = error.response.data.detail;
    } else {
      errorMessage.value = "Invalid email or password.";
    }
  } finally {
    isLoading.value = false;
  }
};
</script>

<style scoped>
.form-section {
  max-height: calc(100vh - 200px);
}

.overall-height {
  min-height: calc(100vh - 0px);
}
</style>
